import React, { useEffect, useRef } from 'react';
import { isCompositeComponentWithType } from 'react-dom/test-utils';
import { useFormContext, UseFormRegisterReturn, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { optionCSS } from 'react-select/dist/declarations/src/components/Option';
import { actions, getCurrentStep, getQuote } from '../../../features/quote/QuoteSlice';
import { Field } from '../../../types';
import useModal from '../../hooks/useModal';
import Modal from '../modal/Modal';
import Tooltip from '../Tooltip';

export interface SelectProps {
    field: Field;
    error?: string;
    inputRef: UseFormRegisterReturn;
}

function SelectInput(props: SelectProps) {
  const { field, error, inputRef } = props;
  const {
    numberOfEmployees, fieldOptions, data, totalNumberOfStaff, notes,
    numberOfOffShore,
  } = useSelector(getQuote);
  const {
    setValue, control,
  } = useFormContext();
  const dispatch = useDispatch();
  const oilOccupation = 'quote_prop_42-104';
  const {
    modalOpen, setOpenModal,
  } = useModal({
    initialMode: false,
    count: 1,
  });

  useEffect(() => {
    if (fieldOptions[field.formId]) {
      const selectedIndex = fieldOptions[field.formId]
        .findIndex(
          (option: any) => option.value === data[field.formId] || option.value === field.value,
        );
      setValue(inputRef.name, selectedIndex > -1 ? (data[field.formId] || field.value) : 'false');
    }
  }, [fieldOptions[field.formId]]);

  useEffect(() => {
    setValue(inputRef.name, data[field.formId] || field.value || 'false');
  }, []);

  const selectValue = useWatch({ control, name: inputRef.name });
  let selectedOptionValue : any = null;
  if (field.options) {
    if (Array.isArray(field.options)) {
      selectedOptionValue = field.options.find((option: any) => option.value === selectValue);
    } else {
      Object.keys(field.options).forEach((option: any) => {
        if (field.options[option].value === selectValue) {
          selectedOptionValue = field.options[option];
        }
      });
    }
  }

  useEffect(() => {
    if (selectValue && selectValue !== 'false') {
      if (window.quickInfoData) {
        if (window.quickInfoData.numberOfFeeEarnersCtrl === field.formId) {
          dispatch(actions.updateNumberOfFeeEarners({
            value: selectedOptionValue.text,
          }));
        }
        if (window.quickInfoData.numberOfAdminCtrl === field.formId) {
          dispatch(actions.updateNumberOfAdminStaff({
            value: selectedOptionValue.text,
          }));
        }
        if (window.quickInfoData.numberOfNoneAdminCtrl === field.formId) {
          dispatch(actions.updateNumberOfNonAdminStaff({
            value: selectedOptionValue.text,
          }));
        }
        if (window.quickInfoData.numberOfEmployeesCtrl === field.formId) {
          dispatch(actions.updateNumberOfEmployees({
            value: selectedOptionValue.text,
          }));
        }
        if (field.formId.indexOf(window.quickInfoData.industryCtrl) > -1) {
          // Update number of off shore earners
          if (selectValue.indexOf(oilOccupation) > -1) {
            let offShoreCount = 0;
            Object.keys(data).forEach((key: string) => {
              if (
                key.indexOf(window.quickInfoData.industryCtrl) > -1
                && data[key].indexOf(oilOccupation) > -1
              ) {
                offShoreCount += 1;
              }
            });
            dispatch(actions.updateNumberOfOffShore({ value: offShoreCount }));
          }
        }
      }
    }
  }, [selectValue]);

  useEffect(() => {
    if (window.quickInfoData) {
      if (window.quickInfoData.numberOfFeeEarnersCtrl === field.formId
        && totalNumberOfStaff === 0) {
        setValue(inputRef.name, fieldOptions[field.formId].find(
          (option: any) => option.value === 'false' || !option.value,
        ).value);
      }
      if (window.quickInfoData.numberOfAdminCtrl === field.formId
          && totalNumberOfStaff === 0) {
        setValue(inputRef.name, fieldOptions[field.formId].find(
          (option: any) => option.value === 'false' || !option.value,
        ).value);
      }
      if (window.quickInfoData.numberOfNoneAdminCtrl === field.formId
          && totalNumberOfStaff === 0) {
        setValue(inputRef.name, fieldOptions[field.formId].find(
          (option: any) => option.value === 'false' || !option.value,
        ).value);
      }
    }
  }, [numberOfEmployees]);

  const keys = fieldOptions[field.formId]
    ? Object.keys(fieldOptions[field.formId]) : [];

  return (
    <div className={`mb-4 form-card ${field.formId} p-4 ${error ? 'error' : ''}`}>
      <label htmlFor={field.id} className="form-label my-2">
        {field.label}
        {field.required
                    && <span className="ms-2 d-inline-block asterix">*</span>}
      </label>
      <select
        data-name={`${field.formId}`}
        {...inputRef}
        onChange={(e: any) => {
          if (window.quickInfoData) {
            if (field.formId === window.quickInfoData.numberOfEmployeesCtrl) {
              dispatch(actions.updateNumberOfFeeEarners({
                value: 0,
              }));
              dispatch(actions.updateNumberOfAdminStaff({
                value: 0,
              }));
              dispatch(actions.updateNumberOfNonAdminStaff({
                value: 0,
              }));
            }
            if (window.quickInfoData.whereControl) {
              setOpenModal(0, e.target.value === 'My friend');
            }
          }
          inputRef.onChange(e);
        }}
        id={field.formId}
        defaultValue={data[field.formId] || (keys.length > 0 && fieldOptions[field.formId] ? fieldOptions[field.formId][0].value : 'false')}
        className="form-select form-select-lg mb-3"
      >
        {keys.map((key: any) => {
          const option = JSON.parse(
            JSON.stringify(fieldOptions[field.formId][key]),
          );

          delete option.trigger_further_questions;
          delete option.formId;
          delete option.selected;
          delete option.disabled;

          if (window.quickInfoData
              && window.quickInfoData.employeeQuestions.indexOf(field.formId) > -1) {
            if (selectedOptionValue) {
              // Only mark the ones that are higher than it as disabled
              option.disabled = option.text > (selectedOptionValue.value
                ? selectedOptionValue.text : 0)
                              && (option.text - (selectedOptionValue.value
                                ? selectedOptionValue.text : 0))
                              > (numberOfEmployees - totalNumberOfStaff)
                              && option.text > (numberOfEmployees - totalNumberOfStaff);
            } else {
              option.disabled = option.text > (numberOfEmployees - totalNumberOfStaff);
            }
          }
          if (window.quickInfoData
            && field.formId === window.quickInfoData.smeOffshoreWorkersControl) {
            option.disabled = option.text > numberOfOffShore;
          }
          return (
            <option
              key={key}
              {...option}
              dangerouslySetInnerHTML={{ __html: option.text }}
            />
          );
        })}
      </select>

      <div className="form-card-footer d-flex justify-content-between">
        {
                    error
                    && <small className="error" dangerouslySetInnerHTML={{ __html: error }} />
                }
        {field.tooltip && <Tooltip tooltipText={field.tooltip} />}
        {notes[field.formId] && (
        <Modal
          onClose={() => setOpenModal(0, false)}
          showModal={modalOpen[0]}
          modalTitle=""
          modalText={notes[field.formId]}
        />
        ) }
      </div>
    </div>
  );
}

SelectInput.defaultProps = {
  error: false,
};

export default SelectInput;
